import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, LifeBuoy, Layers, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { XentraWordmark } from "@/components/XentraLogo";

export default function PortalLogin() {
  const navigate = useNavigate();

  React.useEffect(() => {
    // If already logged in, bounce to dashboard
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/auth/me`, {
      credentials: "include",
    })
      .then((r) => {
        if (r.ok) navigate("/portal/dashboard", { replace: true });
      })
      .catch(() => {});
  }, [navigate]);

  const onGoogleSignIn = () => {
    // DO NOT HARDCODE THE AUTH URL OR ADD FALLBACKS — Emergent contract
    const redirectUrl = window.location.origin + "/portal/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onMicrosoftSignIn = async () => {
    try {
      const r = await api.get(`/auth/microsoft/login?flow=portal&origin=${encodeURIComponent(window.location.origin)}`);
      window.location.href = r.data.authorization_url;
    } catch (e) {
      console.error("Microsoft login failed", e);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-white" data-testid="portal-login-page">
      <div className="relative hidden lg:flex flex-col justify-between bg-gradient-to-br from-blue-950 via-slate-950 to-slate-900 text-white p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "url(https://images.unsplash.com/photo-1573164713988-8665fc963095?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600)",
          backgroundSize: "cover", backgroundPosition: "center",
        }} />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/80 via-slate-950/60 to-blue-950/70" />

        <div className="relative z-10 flex items-center gap-3">
          <XentraWordmark variant="white" size={10} />
          <div className="text-xs uppercase tracking-[0.25em] text-blue-300 font-mono">Customer Portal</div>
        </div>

        <div className="relative z-10">
          <h1 className="font-heading text-4xl xl:text-5xl font-bold leading-tight tracking-tight">
            Your services.
            <br />
            <span className="text-blue-400">All in one place.</span>
          </h1>
          <p className="mt-6 text-slate-300 text-base max-w-md leading-relaxed">
            View support tickets, ongoing projects, your live Stax products and invoice history — anytime, anywhere.
          </p>
          <div className="mt-12 grid grid-cols-2 gap-4 max-w-md">
            {[
              { icon: LifeBuoy, label: "Support tickets" },
              { icon: Layers, label: "Live products" },
              { icon: FileText, label: "Invoices" },
              { icon: ShieldCheck, label: "Secure access" },
            ].map(({ icon: Icon, label }) => (
              <div key={label}
                className="flex items-center gap-3 rounded-md border border-white/10 bg-white/5 backdrop-blur px-4 py-3">
                <Icon className="h-4 w-4 text-blue-400" />
                <span className="text-sm font-medium text-slate-100">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-slate-400 font-mono">
          A service by xentra.co.uk
        </div>
      </div>

      <div className="flex items-center justify-center p-8 sm:p-12 bg-white">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <XentraWordmark variant="dark" size={8} />
            <div className="text-xs uppercase tracking-[0.25em] text-blue-700 font-mono">Portal</div>
          </div>

          <div className="text-xs uppercase tracking-[0.2em] text-blue-700 font-mono mb-4">Customer access</div>
          <h2 className="font-heading text-3xl font-bold text-slate-900 leading-tight">Sign in to your portal.</h2>
          <p className="mt-3 text-slate-500 text-sm leading-relaxed">
            Use the Google account your Xentra contact has authorised. If you can't sign in, please contact your account manager.
          </p>

          <Button
            data-testid="portal-login-google-btn"
            onClick={onGoogleSignIn}
            className="mt-10 w-full h-12 bg-white text-slate-900 border border-slate-300 hover:bg-slate-50 hover:border-slate-400 shadow-sm transition-all flex items-center justify-center gap-3 rounded-md"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.7-5.5 3.7-3.3 0-6-2.7-6-6.1 0-3.3 2.7-6.1 6-6.1 1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 2.9 14.6 2 12 2 6.9 2 2.7 6.1 2.7 11.7s4.2 9.7 9.3 9.7c5.4 0 8.9-3.8 8.9-9.1 0-.6-.1-1.1-.1-1.6H12z" />
            </svg>
            <span className="font-medium">Continue with Google</span>
          </Button>

          <Button
            data-testid="portal-login-microsoft-btn"
            onClick={onMicrosoftSignIn}
            className="mt-3 w-full h-12 bg-white text-slate-900 border border-slate-300 hover:bg-slate-50 hover:border-slate-400 shadow-sm transition-all flex items-center justify-center gap-3 rounded-md"
          >
            <svg className="h-5 w-5" viewBox="0 0 23 23" aria-hidden="true">
              <rect x="1" y="1" width="10" height="10" fill="#F25022" />
              <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
              <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
              <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
            </svg>
            <span className="font-medium">Continue with Microsoft</span>
          </Button>

          <div className="mt-8 text-xs text-slate-400 leading-relaxed">
            Sessions persist for 7 days. Access is restricted to email addresses pre-approved by Xentra for your account.
          </div>

          <div className="mt-16 pt-6 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
            <a href="/" className="hover:text-blue-700 transition-colors font-mono">← Xentra staff sign-in</a>
            <a href="https://www.xentra.co.uk" target="_blank" rel="noreferrer" className="hover:text-blue-700 transition-colors">
              xentra.co.uk →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
